import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  CalendarDays,
  MapPin,
  Phone,
  User,
  Plus,
  Pencil,
  Trash,
  SquareUserIcon,
  ShoppingCart,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../../lib/auth";
import { addAddress } from "../api/addAddress";
import { editAddress } from "../api/editAddress";
import { deleteAddress } from "../api/deleteAddress";
import { getUserOrders } from "../api/getUserOrders";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../../components/ui/collapsible";

const SingleUserProfile = () => {
  const { user, initializeUser, logout } = useAuth();
  const [addresses, setAddresses] = useState<any>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<any>(null);
  const [userOrders, setUserOrders] = useState([]);
  const OrderStatusBadge = ({ status }: { status: string }) => {
    const statusColors: Record<string, string> = {
      rejected: "bg-red-100 text-red-800",
      pending: "bg-amber-100 text-amber-800",
      accepted: "bg-emerald-100 text-emerald-800",
    };

    const statusText: Record<string, string> = {
      rejected: "مرفوض",
      pending: "قيد الانتظار",
      accepted: "مكتمل",
    };

    return (
      <span
        className={`inline-block rounded-full ${statusColors[status]} px-4 py-1 text-sm font-semibold`}
      >
        {statusText[status] || status}
      </span>
    );
  };

  const [formData, setFormData] = useState({
    phone: "",
    address: "",
    landmark: "",
    name: "",
  });
  const handleUserOrders = async () => {
    try {
      const response = await getUserOrders();
      setUserOrders(response.orders);
    } catch (error) {
      console.error("Error fetching user orders:", error);
      // You can handle the error by showing a message or doing other actions
    }
  };

  // Update addresses when the user data changes
  useEffect(() => {
    if (user?.clients) {
      setAddresses(user.clients);
    }
  }, [user]);
  useEffect(() => {
    handleUserOrders();
  }, []);
  const formatDate = (dateString: any) => {
    return new Date(dateString).toLocaleDateString("ar-IQ", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleInputChange = (e: any) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (editingAddress) {
      try {
        const response = await editAddress(editingAddress.id, formData);
        initializeUser(); // Make sure this fetches the latest user data.
      } catch (error) {
        console.error(error);
      }
    } else {
      try {
        const response = await addAddress(formData);
        initializeUser(); // Make sure this fetches the latest user data.
      } catch (error) {
        console.error(error);
      }
    }
    handleClose();
  };

  const handleEdit = (address: any) => {
    setEditingAddress(address);
    setFormData({
      phone: address.phone,
      address: address.address,
      landmark: address.landmark || "",
      name: address.name,
    });
    setIsOpen(true);
  };

  const handleDelete = async (addressId: any, addresses: any) => {
    try {
      if (addresses.length === 1) {
        const response = await deleteAddress(addressId);
        initializeUser();
      } else {
      }
    } catch {}

    // setAddresses(
    //   addresses.filter((addr: { id: any }) => addr.id !== addressId)
    // );
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingAddress(null);
    setFormData({ phone: "", address: "", landmark: "", name: "" });
  };

  const handleAddNew = () => {
    setEditingAddress(null);
    setFormData({ phone: "", address: "", landmark: "", name: "" });
    setIsOpen(true);
  };

  return (
    <div className="space-y-6 p-6" dir="rtl">
      {/* User Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            الملف الشخصي
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col space-y-2">
            <h3 className="text-xl font-bold">{user?.name}</h3>
            <p className="text-gray-600">{user?.username}</p>
            <div className="flex items-center gap-2">
              <Badge variant={user?.isActive ? "default" : "destructive"}>
                {user?.isActive ? "نشط" : "غير نشط"}
              </Badge>
              <Badge variant="secondary">
                {user?.role.name === "user" ? "مستخدم" : user?.role.name}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CalendarDays className="h-4 w-4" />
              عضو منذ {formatDate(user?.createdAt)}
            </div>
          </div>

          {/* Logout Button */}
          <Button
            variant="destructive" // Optional: You can use "secondary" or another variant if you prefer
            className="flex items-center gap-2 mt-4"
            onClick={() => logout()}
          >
            <LogOut className="h-5 w-5" /> {/* Logout icon */}
            تسجيل الخروج
          </Button>
        </CardContent>
      </Card>

      {/* Address Information Card */}
      <Card>
        <CardHeader className="pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              معلومات العنوان
            </CardTitle>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" onClick={handleAddNew}>
                  <Plus className="h-4 w-4 ml-2" />
                  إضافة عنوان
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]" dir="rtl">
                <DialogHeader>
                  <DialogTitle>
                    {editingAddress ? "تعديل العنوان" : "إضافة عنوان جديد"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">الاسم</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">رقم الهاتف</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">العنوان</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="landmark">علامة مميزة</Label>
                    <Input
                      id="landmark"
                      name="landmark"
                      value={formData.landmark}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                    >
                      إلغاء
                    </Button>
                    <Button type="submit">
                      {editingAddress ? "تحديث" : "إضافة"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="mt-6">
          {addresses.map((client: any) => (
            <div
              key={client.id}
              className="space-y-10 border-b py-6 last:border-0"
            >
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <SquareUserIcon className="h-4 w-4 text-gray-500" />
                      <span>{client.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span>{client.phone}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span>{client.address}</span>
                    </div>
                    {client.landmark && (
                      <div className="text-sm text-gray-600">
                        <span className="font-semibold">علامة مميزة:</span>{" "}
                        {client.landmark}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(client)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(client.id, addresses)}
                      disabled={addresses.length === 1}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {addresses.length === 0 && (
            <p className="text-center text-gray-500">لا توجد عناوين مضافة</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-0">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              الطلبات السابقة
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="mt-6">
          {userOrders.map((userOrder: any) => (
            <div
              key={userOrder.id}
              className="space-y-6 border-b py-6 last:border-0"
            >
              {/* Order Details */}
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="font-semibold">تاريخ الإنشاء:</span>
                  <span>
                    {new Date(userOrder.created_at).toLocaleString("ar-EG")}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">الحالة:</span>
                  <OrderStatusBadge status={userOrder.status} />
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">السعر الإجمالي:</span>
                  <span>{userOrder.total_price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">العميل:</span>
                  <span>{userOrder.client?.name || "غير معروف"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">رقم الهاتف:</span>
                  <span>{userOrder.client?.phone || "غير متوفر"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold">العنوان:</span>
                  <span>{userOrder.client?.address || "غير متوفر"}</span>
                </div>
              </div>

              {/* Collapsible Section for Products */}
              <Collapsible>
                <CollapsibleTrigger className="cursor-pointer text-blue-500 underline">
                  عرض المنتجات
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-2 mt-4">
                  {userOrder.products.map((product: any, index: number) => (
                    <div key={index} className="border p-4 rounded-md">
                      <div className="flex justify-between">
                        <span className="font-semibold">اسم المنتج:</span>
                        <span>{product.name_ar}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold">الكمية:</span>
                        <span>{product.pivot.quantity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold">السعر:</span>
                        <span>{product.pivot.price}</span>
                      </div>
                    </div>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}
          {addresses.length === 0 && (
            <p className="text-center text-gray-500">لا توجد عناوين مضافة</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SingleUserProfile;
